import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Process', href: '#process' },
    { name: 'Technologies', href: '#technologies' },
    { name: 'Industries', href: '#industries' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <div className="flex items-center space-x-3">
              <img 
                src="/Logo Icon 1.png" 
                alt="Scale My Cloud" 
                className="h-8 w-8 lg:h-10 lg:w-10"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span className={`text-xl lg:text-2xl font-bold ${
                isScrolled ? 'text-secondary-900' : 'text-white'
              }`}>
                Scale My Cloud
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                className={`font-medium transition-colors duration-200 ${
                  isScrolled 
                    ? 'text-secondary-700 hover:text-primary-600' 
                    : 'text-white hover:text-primary-200'
                }`}
              >
                {item.name}
              </button>
            ))}
            <button 
              onClick={() => scrollToSection('#contact')}
              className="btn-primary"
            >
              Get Started
            </button>
          </nav>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-md ${
                isScrolled ? 'text-secondary-700' : 'text-white'
              }`}
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <button
              type="button"
              className="fixed inset-0 bg-black bg-opacity-40 z-40 lg:hidden"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu backdrop"
            />
            {/* Menu */}
            <nav
              id="mobile-navigation"
              className="fixed inset-0 z-50 flex flex-col justify-center items-center bg-white lg:hidden overflow-y-auto"
            >
              <div className="w-full max-w-sm mx-auto px-6 py-8 rounded-lg shadow-xl">
                {navItems.map((item) => (
                  <button
                    type="button"
                    key={item.name}
                    onClick={() => scrollToSection(item.href)}
                    className="block w-full text-left px-4 py-4 text-lg text-secondary-700 hover:text-primary-600 font-medium mb-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {item.name}
                  </button>
                ))}
                <button 
                  type="button"
                  onClick={() => scrollToSection('#contact')}
                  className="btn-primary w-full mt-6 py-4 text-lg"
                >
                  Get Started
                </button>
                <button
                  type="button"
                  onClick={() => setIsMenuOpen(false)}
                  className="w-full mt-6 text-secondary-400 hover:text-primary-600 text-center text-base"
                  aria-label="Close menu"
                >
                  Close
                </button>
              </div>
            </nav>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
